/**
 * Product Gallery Video Pause Functionality
 * Pauses any playing video when user navigates to another gallery item
 * Supports autoplay functionality based on section settings
 */

(function() {
  'use strict';

  // Configuration
  var isInitialized = false;
  var autoplayEnabled = false;
  var DEBUG = false; // Set to true for development debugging

  // Consolidated selectors
  var SELECTORS = {
    productGallery: '.product__images',
    productVideos: '.product__images video, .product-media__video video, .about-video--frame video, .notes-with-video video',
    youtubeIframes: '.product__images iframe[src*="youtube.com"], .product-media__video-iframe.youtube iframe, .custom-iframe__video iframe[src*="youtube.com"], .about-video--frame iframe[src*="youtube.com"], .notes-with-video iframe[src*="youtube.com"]',
    vimeoIframes: '.product__images iframe[src*="vimeo.com"], .product-media__video-iframe.vimeo iframe, .custom-iframe__video iframe[src*="vimeo.com"], .vimeo_iframe, .about-video--frame iframe[src*="vimeo.com"], .notes-with-video iframe[src*="vimeo.com"]',
    videoFrames: '.product__images .video_frame, .product__images iframe.video_frame, .product__images .video_slide__container iframe',
    // Selectors for videos in product content areas (description and block metafields)
    contentVideos: '.product__description.rte video, .desc-tab-content.rte video, .rte__video-wrapper video',
    contentYoutubeIframes: '.product__description.rte iframe[src*="youtube.com"], .desc-tab-content.rte iframe[src*="youtube.com"], .rte__video-wrapper iframe[src*="youtube.com"]',
    contentVimeoIframes: '.product__description.rte iframe[src*="vimeo.com"], .desc-tab-content.rte iframe[src*="vimeo.com"], .rte__video-wrapper iframe[src*="vimeo.com"]',
    thumbnails: '.product__thumbnails .product__image',
    navButtons: '.product-images__prev-btn, .product-images__next-btn',
    activeSlide: '.product__images .slick-current'
  };

  // Utility function for debug logging
  function log(message, data) {
    if (DEBUG) {
      console.log('Product Gallery Video Pause: ' + message, data || '');
    }
  }

  // Function to check if autoplay is enabled from section settings
  function checkAutoplaySettings() {
    var autoplaySettings = window.productAutoplaySettings || {};
    autoplayEnabled = autoplaySettings.youtube_autoplay || autoplaySettings.vimeo_autoplay || autoplaySettings.html5_autoplay;
    
    // Also check for any autoplay parameters in video URLs (gallery and content areas)
    var allVideoSelectors = [
      SELECTORS.productVideos,
      SELECTORS.youtubeIframes,
      SELECTORS.vimeoIframes,
      SELECTORS.contentVideos,
      SELECTORS.contentYoutubeIframes,
      SELECTORS.contentVimeoIframes
    ].join(', ');
    
    var videos = document.querySelectorAll(allVideoSelectors);
    videos.forEach(function(video) {
      if (video.src && (video.src.includes('autoplay=1') || video.hasAttribute('autoplay'))) {
        autoplayEnabled = true;
      }
    });
    
    log('Autoplay enabled:', autoplayEnabled);
  }

  // Function to ensure YouTube iframes have the enablejsapi parameter
  function ensureYouTubeApiEnabled() {
    var allYoutubeSelectors = SELECTORS.youtubeIframes + ', ' + SELECTORS.contentYoutubeIframes;
    var youtubeIframes = document.querySelectorAll(allYoutubeSelectors);
    youtubeIframes.forEach(function(iframe) {
      if (iframe.src && iframe.src.indexOf('enablejsapi=1') === -1) {
        var separator = iframe.src.indexOf('?') !== -1 ? '&' : '?';
        iframe.src = iframe.src + separator + 'enablejsapi=1&origin=' + window.location.origin;
        log('Added enablejsapi=1 to YouTube iframe');
      }
    });
  }

  // Function to pause all videos in the product gallery
  function pauseAllProductVideos() {
    try {
      var pausedCount = 0;

      // Pause HTML5 videos
      var videos = document.querySelectorAll(SELECTORS.productVideos);
      videos.forEach(function(video) {
        if (video && !video.paused) {
          video.pause();
          pausedCount++;
          log('Paused HTML5 video');
        }
      });

      // Pause Vimeo videos
      var vimeoIframes = document.querySelectorAll(SELECTORS.vimeoIframes);
      vimeoIframes.forEach(function(iframe) {
        if (iframe && iframe.contentWindow && iframe.contentWindow.postMessage) {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          pausedCount++;
          log('Paused Vimeo video');
        }
      });

      // Pause other video frames
      var videoFrames = document.querySelectorAll(SELECTORS.videoFrames);
      videoFrames.forEach(function(iframe) {
        if (iframe && iframe.contentWindow && iframe.contentWindow.postMessage) {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          pausedCount++;
          log('Paused video frame');
        }
      });

      // Pause YouTube videos
      pauseYouTubeVideos();

      log('Paused ' + pausedCount + ' videos (non-YouTube)');

    } catch (error) {
      console.warn('Error pausing videos:', error);
    }
  }

  // Function to pause YouTube videos
  function pauseYouTubeVideos() {
    var youtubeIframes = document.querySelectorAll(SELECTORS.youtubeIframes);
    var pausedCount = 0;
    
    youtubeIframes.forEach(function(iframe) {
      if (iframe && iframe.contentWindow && iframe.contentWindow.postMessage) {
        try {
          // Primary YouTube pause command
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          
          // Fallback YouTube pause command
          iframe.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
          
          // Try YouTube IFrame API if available
          if (typeof YT !== 'undefined' && YT.Player && window.youtubePlayers) {
            var playerId = iframe.id;
            if (playerId && window.youtubePlayers[playerId]) {
              window.youtubePlayers[playerId].pauseVideo();
            }
          }
          
          pausedCount++;
          log('Paused YouTube video');
        } catch (error) {
          console.warn('Error pausing YouTube video:', error);
        }
      }
    });
    
    log('Paused ' + pausedCount + ' YouTube videos');
  }

  // Function to handle slide changes with autoplay consideration
  function handleSlideChange() {
    if (autoplayEnabled) {
      // If autoplay is enabled, wait to allow autoplay to work properly
      setTimeout(function() {
        log('Slide change with autoplay enabled, checking videos...');
        
        // Only pause videos if they're not supposed to autoplay
        var activeSlide = document.querySelector(SELECTORS.activeSlide);
        if (activeSlide) {
          var videos = activeSlide.querySelectorAll('video, iframe');
          var shouldPause = true;
          
          videos.forEach(function(video) {
            if (video.hasAttribute('autoplay') || (video.src && video.src.includes('autoplay=1'))) {
              shouldPause = false;
            }
          });
          
          if (shouldPause) {
            log('No autoplay videos detected, pausing all videos');
            pauseAllProductVideos();
          } else {
            log('Autoplay videos detected, not pausing');
          }
        }
      }, 3000); // 3 second delay to allow autoplay to work
    } else {
      // If autoplay is disabled, pause immediately
      log('Slide change detected, pausing videos immediately');
      pauseAllProductVideos();
    }
  }

  // Function to enable autoplay for videos in the current slide
  function enableVideoAutoplay() {
    if (!autoplayEnabled) {
      return;
    }

    var activeSlide = document.querySelector(SELECTORS.activeSlide);
    if (!activeSlide) {
      return;
    }

    log('Enabling autoplay for active slide');

    // Autoplay HTML5 videos
    var videos = activeSlide.querySelectorAll('video');
    videos.forEach(function(video) {
      if (video.paused && video.hasAttribute('autoplay')) {
        video.muted = true; // Ensure muted for autoplay
        video.play().catch(function(error) {
          console.warn('Could not autoplay HTML5 video:', error);
        });
        log('Attempting to autoplay HTML5 video');
      }
    });

    // Autoplay iframe videos
    var iframes = activeSlide.querySelectorAll('iframe');
    iframes.forEach(function(iframe) {
      if (iframe.src && iframe.src.includes('autoplay=1')) {
        log('Autoplay iframe detected');
        
        // YouTube iframes
        if (iframe.src.includes('youtube.com') && iframe.contentWindow && iframe.contentWindow.postMessage) {
          setTimeout(function() {
            try {
              iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
              log('Sent play command to YouTube iframe');
            } catch (error) {
              console.warn('Could not send play command to YouTube iframe:', error);
            }
          }, 500);
        }
        
        // Vimeo iframes
        if (iframe.src.includes('vimeo.com') && iframe.contentWindow && iframe.contentWindow.postMessage) {
          setTimeout(function() {
            try {
              iframe.contentWindow.postMessage('{"method":"play"}', '*');
              log('Sent play command to Vimeo iframe');
            } catch (error) {
              console.warn('Could not send play command to Vimeo iframe:', error);
            }
          }, 500);
        }
      }
    });
  }

  // Function to enable autoplay for videos in content areas (description and block metafields)
  function enableContentVideoAutoplay() {
    if (!autoplayEnabled) {
      return;
    }

    log('Enabling autoplay for content area videos');

    // Autoplay HTML5 videos in content areas
    var contentVideos = document.querySelectorAll(SELECTORS.contentVideos);
    contentVideos.forEach(function(video) {
      if (video.paused && video.hasAttribute('autoplay')) {
        video.muted = true; // Ensure muted for autoplay
        video.play().catch(function(error) {
          console.warn('Could not autoplay content HTML5 video:', error);
        });
        log('Attempting to autoplay content HTML5 video');
      }
    });

    // Autoplay YouTube iframes in content areas
    var contentYoutubeIframes = document.querySelectorAll(SELECTORS.contentYoutubeIframes);
    contentYoutubeIframes.forEach(function(iframe) {
      if (iframe.src && iframe.src.includes('autoplay=1') && iframe.contentWindow && iframe.contentWindow.postMessage) {
        setTimeout(function() {
          try {
            iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            log('Sent play command to content YouTube iframe');
          } catch (error) {
            console.warn('Could not send play command to content YouTube iframe:', error);
          }
        }, 500);
      }
    });

    // Autoplay Vimeo iframes in content areas
    var contentVimeoIframes = document.querySelectorAll(SELECTORS.contentVimeoIframes);
    contentVimeoIframes.forEach(function(iframe) {
      if (iframe.src && iframe.src.includes('autoplay=1') && iframe.contentWindow && iframe.contentWindow.postMessage) {
        setTimeout(function() {
          try {
            iframe.contentWindow.postMessage('{"method":"play"}', '*');
            log('Sent play command to content Vimeo iframe');
          } catch (error) {
            console.warn('Could not send play command to content Vimeo iframe:', error);
          }
        }, 500);
      }
    });
  }

  // Function to add event handlers for navigation
  function addNavigationHandlers(productGallery) {
    // Handle thumbnail clicks
    var thumbnails = document.querySelectorAll(SELECTORS.thumbnails);
    thumbnails.forEach(function(thumbnail) {
      thumbnail.addEventListener('click', function() {
        setTimeout(function() {
          log('Thumbnail click detected');
          handleSlideChange();
        }, 150);
      });
    });

    // Handle navigation button clicks
    var navButtons = document.querySelectorAll(SELECTORS.navButtons);
    navButtons.forEach(function(button) {
      button.addEventListener('click', function() {
        setTimeout(function() {
          log('Navigation button clicked');
          handleSlideChange();
        }, 150);
      });
    });

    // Handle swipe events
    productGallery.addEventListener('touchstart', function() {
      this.touchStartTime = Date.now();
    });

    productGallery.addEventListener('touchend', function() {
      if (this.touchStartTime && (Date.now() - this.touchStartTime) < 300) {
        setTimeout(function() {
          log('Swipe detected');
          handleSlideChange();
        }, 150);
      }
    });

    // Handle keyboard navigation
    document.addEventListener('keydown', function(event) {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        var productSection = document.querySelector('.product');
        if (productSection && !productSection.classList.contains('hidden')) {
          setTimeout(function() {
            log('Keyboard navigation detected');
            handleSlideChange();
          }, 150);
        }
      }
    });

    // Handle mouse wheel navigation
    productGallery.addEventListener('wheel', function(event) {
      if (Math.abs(event.deltaX) > 10 || Math.abs(event.deltaY) > 10) {
        setTimeout(function() {
          log('Mouse wheel navigation detected');
          handleSlideChange();
        }, 150);
      }
    });
  }

  // Function to initialize video pause functionality
  function initProductGalleryVideoPause() {
    if (isInitialized) {
      return;
    }

    var productGallery = document.querySelector(SELECTORS.productGallery);
    if (!productGallery) {
      return;
    }

    log('Initializing video pause functionality');

    // Check autoplay settings and ensure YouTube API is enabled
    checkAutoplaySettings();
    ensureYouTubeApiEnabled();

    // Wait for slick slider to be initialized
    var checkSlickInterval = setInterval(function() {
      if (productGallery.classList.contains('slick-initialized')) {
        clearInterval(checkSlickInterval);
        
        log('Slick slider detected, setting up event listeners');
        
        // Add slick slider event listeners
        if (typeof $ !== 'undefined') {
          $(productGallery).on('beforeChange', function() {
            handleSlideChange();
          });
          
          $(productGallery).on('afterChange', function() {
            if (autoplayEnabled) {
              setTimeout(function() {
                log('Slide changed, enabling autoplay for new slide');
                enableVideoAutoplay();
              }, 100);
            }
          });
        }

        // Add navigation handlers
        addNavigationHandlers(productGallery);

        isInitialized = true;
        log('Initialization complete');
        
        // Enable autoplay for the initial slide and content videos if autoplay is enabled
        if (autoplayEnabled) {
          setTimeout(function() {
            log('Initial load, enabling autoplay');
            enableVideoAutoplay(); // Gallery videos
            enableContentVideoAutoplay(); // Content area videos
            
            // Retry after delay to ensure videos start
            setTimeout(function() {
              enableVideoAutoplay();
              enableContentVideoAutoplay();
            }, 2000);
          }, 500);
        }
      }
    }, 100);

    // Fallback: if slick doesn't initialize within 5 seconds
    setTimeout(function() {
      if (!productGallery.classList.contains('slick-initialized')) {
        clearInterval(checkSlickInterval);
        log('Slick slider not detected, using fallback');
        
        // Add basic navigation handlers
        addNavigationHandlers(productGallery);
        
        // Enable autoplay for content videos even without slick
        if (autoplayEnabled) {
          setTimeout(function() {
            log('Fallback: enabling autoplay for content videos');
            enableContentVideoAutoplay();
          }, 500);
        }
        
        isInitialized = true;
        log('Fallback initialization complete');
      }
    }, 5000);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductGalleryVideoPause);
  } else {
    initProductGalleryVideoPause();
  }

  // Also initialize when Shopify's section:load event fires (for dynamic content)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.classList.contains('product')) {
      isInitialized = false; // Reset for new section
      setTimeout(initProductGalleryVideoPause, 100);
    }
  });

  // Handle AJAX page loads
  document.addEventListener('shopify:section:reload', function(event) {
    if (event.target.classList.contains('product')) {
      isInitialized = false; // Reset for new section
      setTimeout(initProductGalleryVideoPause, 100);
    }
  });

})();
